import { csv, number } from './liveModel';
import { SAVED_SOURCE_MEMBERS } from './sourceMembers';

export const PAGE_ID = 'qr-campaigns';
export const CARD_ID = 'qr-campaign-rows';
export const SUMMARY_VIEW = 'QR Campaigns Summary';
export const INSIGHTS_VIEW = 'QR Campaign Insights';
export const VIEWS = [SUMMARY_VIEW, INSIGHTS_VIEW] as const;

const RED_GREEN_GOLD = ['#be2a3e', '#e25f48', '#f88f4d', '#f4d166', '#90b960', '#4b9b5f', '#22763f'] as const;

const SAVED_SOURCES = new Set<string>(SAVED_SOURCE_MEMBERS);

export type Multi = 'all' | string[];

export type SummaryScope = {
  from: string | null;
  to: string | null;
  sources: 'saved' | Multi;
  campaigns: Multi;
  countries: Multi;
  states: Multi;
};

export type InsightsScope = {
  from: string | null;
  to: string | null;
  sources: 'saved' | Multi;
  campaigns: Multi;
  action: string | null;
};

export type Fact = {
  campaignDate: string;
  campaignWeek: string;
  dayName: string;
  campaign: string;
  mktSource: string;
  source: string;
  country: string;
  state: string;
  showDate: boolean;
  sessions: number;
  users: number;
  newUsers: number;
  bounces: number;
  views: number;
  sessionsDuration: number;
  orders: number;
  ttv: number;
  grossMargin: number;
};

export type ResultRow = {
  campaignWeek: string;
  source: string;
  dayName: string;
  campaign: string;
  mktSource: string;
  rank: number;
  users: number;
  newUsers: number;
  sessions: number;
  bounces: number;
  views: number;
  sessionsDuration: number;
  orders: number;
  ttv: number;
  grossMargin: number;
  bounceRate: number | null;
  pagesPerSession: number | null;
  avgDuration: number | null;
  cvr: number | null;
};

export type SourceBar = {
  source: string;
  users: number;
  sessions: number;
  orders: number;
  ttv: number;
  grossMargin: number;
  cvr: number | null;
};

export type WeekBar = {
  campaignWeek: string;
  users: number;
  sessions: number;
  orders: number;
  ttv: number;
  grossMargin: number;
  cvr: number | null;
};

const RESULT_HEADERS = [
  'Campaign Week',
  'Source',
  'Day',
  'Campaign',
  'Users',
  'New Users',
  'Sessions',
  'Bounce Rate',
  'Page Views / Session',
  'Avg Session Duration (s)',
  'Conversion Rate',
  'TTV',
  'Gross Margin',
] as const;

function unwrap(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in value) return (value as { value: unknown }).value;
  return value;
}

function asDate(value: unknown): string {
  const raw = unwrap(value);
  if (raw == null || raw === '') return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw));
  return match ? match[1] : String(raw);
}

function asNumber(value: unknown): number {
  const n = number(unwrap(value));
  return n ?? 0;
}

function divide(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : numerator / denominator;
}

function allows(value: string, filter: Multi | 'saved'): boolean {
  if (filter === 'all') return true;
  if (filter === 'saved') return SAVED_SOURCES.has(value);
  return filter.includes(value);
}

function inDates(date: string, from: string | null, to: string | null): boolean {
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

export function readFacts(rows: ReadonlyArray<Record<string, unknown>>): Fact[] {
  return rows.flatMap((row) => {
    const campaignDate = asDate(row.campaign_date);
    const campaignWeek = asDate(row.campaign_week);
    if (!campaignDate || !campaignWeek) return [];
    return [
      {
        campaignDate,
        campaignWeek,
        dayName: unwrap(row.day_name) == null ? '' : String(unwrap(row.day_name)),
        campaign: unwrap(row.campaign) == null ? '' : String(unwrap(row.campaign)),
        mktSource: unwrap(row.mkt_source) == null ? '' : String(unwrap(row.mkt_source)),
        source: unwrap(row.source) == null ? '' : String(unwrap(row.source)),
        country: unwrap(row.country) == null ? '' : String(unwrap(row.country)),
        state: unwrap(row.state) == null ? '' : String(unwrap(row.state)),
        showDate: unwrap(row.show_date) === true || unwrap(row.show_date) === 'true',
        sessions: asNumber(row.sessions),
        users: asNumber(row.users),
        newUsers: asNumber(row.new_users),
        bounces: asNumber(row.bounces),
        views: asNumber(row.views),
        sessionsDuration: asNumber(row.sessions_duration),
        orders: asNumber(row.orders),
        ttv: asNumber(row.ttv),
        grossMargin: asNumber(row.gross_margin),
      },
    ];
  });
}

function blankTotals() {
  return {
    users: 0,
    newUsers: 0,
    sessions: 0,
    bounces: 0,
    views: 0,
    sessionsDuration: 0,
    orders: 0,
    ttv: 0,
    grossMargin: 0,
  };
}

function addTotals<T extends ReturnType<typeof blankTotals>>(totals: T, fact: Fact): T {
  totals.users += fact.users;
  totals.newUsers += fact.newUsers;
  totals.sessions += fact.sessions;
  totals.bounces += fact.bounces;
  totals.views += fact.views;
  totals.sessionsDuration += fact.sessionsDuration;
  totals.orders += fact.orders;
  totals.ttv += fact.ttv;
  totals.grossMargin += fact.grossMargin;
  return totals;
}

function withRates<T extends ReturnType<typeof blankTotals>>(totals: T) {
  return {
    ...totals,
    bounceRate: divide(totals.bounces, totals.sessions),
    pagesPerSession: divide(totals.views, totals.sessions),
    avgDuration: divide(totals.sessionsDuration, totals.sessions),
    cvr: divide(totals.orders, totals.sessions),
  };
}

function summaryFacts(facts: readonly Fact[], scope: SummaryScope): Fact[] {
  return facts.filter(
    (fact) =>
      fact.showDate &&
      allows(fact.source, scope.sources) &&
      allows(fact.campaign, scope.campaigns) &&
      allows(fact.country, scope.countries) &&
      allows(fact.state, scope.states) &&
      inDates(fact.campaignDate, scope.from, scope.to),
  );
}

export function weeklyResults(facts: readonly Fact[], scope: SummaryScope): ResultRow[] {
  const grouped = new Map<string, ReturnType<typeof blankTotals> & { mktSources: Set<string>; key: ResultRow }>();
  summaryFacts(facts, scope).forEach((fact) => {
    const id = [fact.campaignWeek, fact.source, fact.dayName, fact.campaign].join('\0');
    const current = grouped.get(id);
    if (!current) {
      grouped.set(id, {
        ...addTotals(blankTotals(), fact),
        mktSources: new Set([fact.mktSource]),
        key: {
          campaignWeek: fact.campaignWeek,
          source: fact.source,
          dayName: fact.dayName,
          campaign: fact.campaign,
          mktSource: fact.mktSource,
          rank: 0,
          ...withRates(blankTotals()),
        },
      });
      return;
    }
    addTotals(current, fact);
    current.mktSources.add(fact.mktSource);
  });
  const rows = [...grouped.values()].map((entry) => ({
    ...entry.key,
    ...withRates(entry),
    mktSource: entry.mktSources.size === 1 ? [...entry.mktSources][0] : '*',
  }));
  const byWeek = new Map<string, ResultRow[]>();
  rows.forEach((row) => {
    const list = byWeek.get(row.campaignWeek) ?? [];
    list.push(row);
    byWeek.set(row.campaignWeek, list);
  });
  byWeek.forEach((list) => {
    list.sort(compareResultRows);
    let previous = Number.NaN;
    let rank = 0;
    list.forEach((row, index) => {
      if (row.users !== previous) rank = index + 1;
      previous = row.users;
      row.rank = rank;
    });
  });
  return rows.sort((a, b) => b.campaignWeek.localeCompare(a.campaignWeek) || compareResultRows(a, b));
}

function compareResultRows(a: ResultRow, b: ResultRow): number {
  return (
    b.users - a.users ||
    a.source.localeCompare(b.source) ||
    a.dayName.localeCompare(b.dayName) ||
    a.campaign.localeCompare(b.campaign)
  );
}

export function sourceBars(facts: readonly Fact[], scope: InsightsScope): SourceBar[] {
  const grouped = new Map<string, ReturnType<typeof blankTotals>>();
  facts.forEach((fact) => {
    if (!allows(fact.source, scope.sources)) return;
    if (scope.action && fact.source !== scope.action) return;
    grouped.set(fact.source, addTotals(grouped.get(fact.source) ?? blankTotals(), fact));
  });
  return [...grouped.entries()]
    .map(([source, totals]) => ({ source, ...withRates(totals) }))
    .sort((a, b) => b.users - a.users || a.source.localeCompare(b.source));
}

export function weeklyBars(facts: readonly Fact[], scope: InsightsScope): WeekBar[] {
  const grouped = new Map<string, ReturnType<typeof blankTotals>>();
  facts.forEach((fact) => {
    if (scope.action && fact.source !== scope.action) return;
    if (!allows(fact.campaign, scope.campaigns)) return;
    if (!inDates(fact.campaignDate, scope.from, scope.to)) return;
    grouped.set(fact.campaignWeek, addTotals(grouped.get(fact.campaignWeek) ?? blankTotals(), fact));
  });
  return [...grouped.entries()]
    .map(([campaignWeek, totals]) => ({ campaignWeek, ...withRates(totals) }))
    .sort((a, b) => a.campaignWeek.localeCompare(b.campaignWeek));
}

export function distinctValues(
  facts: readonly Fact[],
  key: keyof Fact,
  keep: (fact: Fact) => boolean = () => true,
): string[] {
  return [...new Set(facts.filter(keep).map((fact) => String(fact[key])))].sort((a, b) => a.localeCompare(b));
}

/** Export values at the precision the sheets show, so a spreadsheet doesn't carry 15-digit ratios.
 * Rates stay fractions (0.7788 = 77.88%); money keeps cents. */
const RATE_DECIMALS = 4;
const RATIO_DECIMALS = 2;
const DURATION_DECIMALS = 1;
const MONEY_DECIMALS = 2;

function rounded(value: number | null, decimals: number): number | null {
  if (value === null || !Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function resultCsv(rows: readonly ResultRow[]): string {
  const shaped = rows.map((row) => ({
    'Campaign Week': row.campaignWeek,
    Source: row.source,
    Day: row.dayName,
    Campaign: row.campaign,
    Users: row.users,
    'New Users': row.newUsers,
    Sessions: row.sessions,
    'Bounce Rate': rounded(row.bounceRate, RATE_DECIMALS),
    'Page Views / Session': rounded(row.pagesPerSession, RATIO_DECIMALS),
    'Avg Session Duration (s)': rounded(row.avgDuration, DURATION_DECIMALS),
    'Conversion Rate': rounded(row.cvr, RATE_DECIMALS),
    TTV: rounded(row.ttv, MONEY_DECIMALS),
    'Gross Margin': rounded(row.grossMargin, MONEY_DECIMALS),
  }));
  return csv(shaped, [...RESULT_HEADERS]);
}

export function sourceCsv(rows: readonly SourceBar[]): string {
  return csv(
    rows.map((row) => ({
      Source: row.source,
      Users: row.users,
      'Conversion Rate': rounded(row.cvr, RATE_DECIMALS),
      TTV: rounded(row.ttv, MONEY_DECIMALS),
      'Gross Margin': rounded(row.grossMargin, MONEY_DECIMALS),
      Sessions: row.sessions,
    })),
    ['Source', 'Users', 'Conversion Rate', 'TTV', 'Gross Margin', 'Sessions'],
  );
}

export function weekCsv(rows: readonly WeekBar[]): string {
  return csv(
    rows.map((row) => ({
      'Campaign Week': row.campaignWeek,
      Users: row.users,
      'Conversion Rate': rounded(row.cvr, RATE_DECIMALS),
      TTV: rounded(row.ttv, MONEY_DECIMALS),
      'Gross Margin': rounded(row.grossMargin, MONEY_DECIMALS),
      Sessions: row.sessions,
    })),
    ['Campaign Week', 'Users', 'Conversion Rate', 'TTV', 'Gross Margin', 'Sessions'],
  );
}

export function sessionsSum(rows: readonly { sessions: number }[]): number {
  return rows.reduce((sum, row) => sum + row.sessions, 0);
}

export function resultTotal(rows: readonly ResultRow[]) {
  const totals = blankTotals();
  rows.forEach((row) => {
    totals.users += row.users;
    totals.newUsers += row.newUsers;
    totals.sessions += row.sessions;
    totals.bounces += row.bounces;
    totals.views += row.views;
    totals.sessionsDuration += row.sessionsDuration;
    totals.orders += row.orders;
    totals.ttv += row.ttv;
    totals.grossMargin += row.grossMargin;
  });
  return withRates(totals);
}

function mix(from: string, to: string, amount: number): string {
  const channel = (hex: string, offset: number) => Number.parseInt(hex.slice(offset, offset + 2), 16);
  const value = (offset: number) =>
    Math.round(channel(from, offset) + (channel(to, offset) - channel(from, offset)) * amount);
  return `#${[1, 3, 5].map((offset) => value(offset).toString(16).padStart(2, '0')).join('')}`;
}

function paletteAt(amount: number): string {
  const clamped = Math.min(1, Math.max(0, amount));
  const scaled = clamped * (RED_GREEN_GOLD.length - 1);
  const index = Math.min(RED_GREEN_GOLD.length - 2, Math.floor(scaled));
  return mix(RED_GREEN_GOLD[index], RED_GREEN_GOLD[index + 1], scaled - index);
}

export function continuousColor(value: number, min: number, max: number): string {
  if (!(max > min)) return paletteAt(0.5);
  return paletteAt((value - min) / (max - min));
}

export function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${Number(match[3])}/${Number(match[2])}/${match[1]}`;
}

export function formatInteger(value: number): string {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value);
}

export function formatPercent(value: number | null): string {
  if (value === null) return '';
  return `${(value * 100).toFixed(1)}%`;
}

export function formatCurrency(value: number): string {
  const rounded = Math.round(Math.abs(value)).toLocaleString('en-AU');
  return value < 0 ? `-$${rounded}` : `$${rounded}`;
}

export function measureText(row: ResultRow, key: (typeof RESULT_HEADERS)[number]): string {
  if (key === 'Campaign Week') return row.campaignWeek;
  if (key === 'Source') return row.source;
  if (key === 'Day') return row.dayName;
  if (key === 'Campaign') return row.campaign;
  if (key === 'Users') return formatInteger(row.users);
  if (key === 'New Users') return formatInteger(row.newUsers);
  if (key === 'Sessions') return formatInteger(row.sessions);
  if (key === 'Bounce Rate') return formatPercent(row.bounceRate);
  if (key === 'Page Views / Session') return row.pagesPerSession === null ? '' : formatInteger(row.pagesPerSession);
  if (key === 'Avg Session Duration (s)') return row.avgDuration === null ? '' : formatInteger(row.avgDuration);
  if (key === 'Conversion Rate') return formatPercent(row.cvr);
  if (key === 'TTV') return formatCurrency(row.ttv);
  return formatCurrency(row.grossMargin);
}

export const RESULT_MEASURES = RESULT_HEADERS.slice(4);

export function sameMembers(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const values = new Set(right);
  return left.every((value) => values.has(value));
}
